import { useEffect, useMemo, useState, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Search, X } from "lucide-react";
import { api } from "@/lib/api";
import ProductCard from "@/components/ProductCard";
import { groupProducts, baseName } from "@/lib/group";

const SORTS = [
  { value: "featured", label: "Featured" },
  { value: "price-asc", label: "Price: Low to High" },
  { value: "price-desc", label: "Price: High to Low" },
  { value: "name", label: "Name: A to Z" },
];

export default function Shop() {
  const [products, setProducts] = useState([]);
  const [cat, setCat] = useState("All");
  const [query, setQuery] = useState("");
  const [sort, setSort] = useState("featured");
  const [showSug, setShowSug] = useState(false);
  const boxRef = useRef(null);
  const navigate = useNavigate();
  useEffect(() => { api.get("/products").then(({ data }) => setProducts(data)).catch(() => {}); }, []);

  useEffect(() => {
    const onClick = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setShowSug(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const cats = ["All", ...Array.from(new Set(products.map((p) => p.category)))];

  const matches = (p, q) => !q || [p.name, p.category, p.short_desc, p.subtitle].filter(Boolean)
    .some((f) => f.toLowerCase().includes(q));

  const groups = useMemo(() => {
    const q = query.trim().toLowerCase();
    const inCat = products.filter((p) => cat === "All" || p.category === cat);
    const filtered = inCat.filter((p) => matches(p, q));
    let g = groupProducts(filtered);
    if (sort === "price-asc") g = [...g].sort((a, b) => a.rep.price - b.rep.price);
    else if (sort === "price-desc") g = [...g].sort((a, b) => b.variants[b.variants.length - 1].price - a.variants[a.variants.length - 1].price);
    else if (sort === "name") g = [...g].sort((a, b) => baseName(a.rep.name).localeCompare(baseName(b.rep.name)));
    return g;
  }, [products, cat, query, sort]);

  // Autocomplete suggestions (grouped, name-based)
  const suggestions = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return [];
    const seen = new Set();
    const out = [];
    for (const p of products) {
      if (!matches(p, q)) continue;
      const key = p.group || p.slug;
      if (seen.has(key)) continue;
      seen.add(key);
      out.push(p);
      if (out.length >= 6) break;
    }
    return out;
  }, [products, query]);

  return (
    <div>
      <div className="border-b border-ape-border bg-ape-surface">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-16">
          <span className="font-mono2 text-xs tracking-[0.3em] text-ape-red">RESEARCH USE ONLY</span>
          <h1 className="font-display text-5xl sm:text-7xl tracking-tight mt-2">THE ARSENAL</h1>
          <p className="text-ape-muted mt-3 max-w-xl">Premium research peptides — third-party tested, 99%+ purity, batch verified. Choose your compound.</p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-12">
        {/* Search + Sort */}
        <div className="flex flex-col lg:flex-row gap-3 mb-8">
          <div className="relative flex-1" ref={boxRef}>
            <Search size={18} className="absolute left-4 top-4 text-ape-muted z-10" />
            <input value={query} onChange={(e) => { setQuery(e.target.value); setShowSug(true); }}
              onFocus={() => setShowSug(true)} placeholder="Search peptides…" data-testid="shop-search"
              className="w-full bg-transparent border-2 border-ape-border rounded-none pl-11 pr-10 py-3 text-white font-mono2 text-sm focus:border-ape-red focus:outline-none transition-colors duration-150" />
            {query && (
              <button onClick={() => { setQuery(""); setShowSug(false); }} data-testid="shop-search-clear"
                className="absolute right-3 top-3.5 text-ape-muted hover:text-ape-red transition-colors duration-150"><X size={16} /></button>
            )}
            {showSug && suggestions.length > 0 && (
              <div className="absolute z-30 left-0 right-0 mt-1 bg-ape-bg border-2 border-ape-border max-h-80 overflow-auto" data-testid="search-suggestions">
                {suggestions.map((s) => (
                  <button key={s.group || s.slug} onMouseDown={() => navigate(`/product/${s.slug}`)}
                    data-testid={`suggestion-${s.slug}`}
                    className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-ape-surface text-left transition-colors duration-150 border-b border-ape-border last:border-0">
                    <img src={s.image} alt="" className="h-9 w-9 object-contain shrink-0" />
                    <div className="min-w-0">
                      <div className="text-sm text-white truncate">{baseName(s.name)}</div>
                      <div className="font-mono2 text-[10px] text-ape-muted uppercase tracking-widest">{s.category}</div>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
          <select value={sort} onChange={(e) => setSort(e.target.value)} data-testid="shop-sort"
            className="bg-ape-bg border-2 border-ape-border rounded-none px-4 py-3 text-white font-mono2 text-sm uppercase tracking-widest focus:border-ape-red focus:outline-none transition-colors duration-150 cursor-pointer">
            {SORTS.map((s) => <option key={s.value} value={s.value} className="bg-ape-bg">{s.label}</option>)}
          </select>
        </div>

        {/* Category filters */}
        <div className="flex flex-wrap gap-3 mb-8">
          {cats.map((c) => (
            <button key={c} onClick={() => setCat(c)} data-testid={`filter-${c.toLowerCase()}`}
              className={`font-head uppercase tracking-widest text-sm px-5 py-2.5 border transition-colors duration-150 ${cat === c ? "bg-ape-red border-ape-red text-white" : "border-ape-border text-ape-muted hover:text-white hover:border-white"}`}>
              {c}
            </button>
          ))}
        </div>

        <div className="font-mono2 text-xs text-ape-muted mb-6" data-testid="shop-result-count">
          {groups.length} {groups.length === 1 ? "PRODUCT" : "PRODUCTS"}
        </div>

        {groups.length === 0 ? (
          <div className="text-center py-20 text-ape-muted font-mono2" data-testid="shop-empty">
            No peptides match your search. Try a different term.
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {groups.map((g, i) => <ProductCard key={g.key} p={g.rep} variants={g.variants} index={i} />)}
          </div>
        )}
      </div>
    </div>
  );
}
